import { APP_ROUTES, ROUTE_BUILDER, toSlugId } from '@/constants/pages';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { PlaceService } from '@/types/state/shared';
import { History } from 'history';
import { matchPath } from 'react-router-dom';
import { BOOKING_FLOW_STEP_SLUGS, BookingFlowStep } from './constants';
import { BookingFlowSelectionPatch, serialiseSelection } from './urlState';

/** Navigate to one of the four booking-flow step pages, keeping the current place. */
export type BookingFlowStepTarget = { type: 'step'; step: BookingFlowStep };
/**
 * Exit the booking flow into the calendar for a main service. `mainService` only needs the
 * fields `ROUTE_BUILDER.bookingCalendar` requires to build the `serviceSlugId` segment.
 */
export type BookingFlowCalendarTarget = {
  type: 'calendar';
  mainService: Pick<PlaceService, 'id'> & { about?: Pick<NonNullable<PlaceService['about']>, 'slug'> };
};

export type BookingFlowGoToStepTarget = BookingFlowStepTarget | BookingFlowCalendarTarget;

/**
 * History state carried by in-flow step pushes: which step this entry was navigated from,
 * and how many history entries back that screen is (> 1 when same-step pushes, e.g. add-on
 * picks on /tillval, sit in between). The header back button renders `fromStep`'s title and
 * calls `history.go(-backSteps)`; an entry without this state is the screen the user entered
 * the flow at, where the button means "Avbryt". Entry pushes (`enterBookingFlow`) and guard
 * redirects (`buildTargetLocation`) deliberately carry no stamp.
 *
 * `fromStep` also takes `CALENDAR_ORIGIN`: the calendar pushes into /valj-utovare, and without
 * a stamp that screen's Avbryt would replace to the place page — discarding the picked time and
 * leaving the abandoned calendar reachable through browser-back.
 */
export type BookingFlowStepHistoryState = { fromStep: BookingFlowStepOrigin; backSteps: number };

/** The calendar as a `fromStep` origin — it is a page of the flow, but not one of its steps. */
export const CALENDAR_ORIGIN = 'calendar';

/** Where an in-flow push came from: a step page, or the calendar. */
export type BookingFlowStepOrigin = BookingFlowStep | typeof CALENDAR_ORIGIN;

/**
 * Both history-state shapes a booking-flow page can be reached with, merged: the step
 * rewind stamp (`{ fromStep, backSteps }`) and the calendar exit's (`{ fromLanding,
 * fromStep? }`). Read `location.state` through {@link readBookingFlowHistoryState} rather
 * than asserting its shape at each call site — react-router types `location.state` as
 * `unknown`, so the one unavoidable narrowing lives here.
 */
export type BookingFlowHistoryState = Partial<BookingFlowStepHistoryState> & { fromLanding?: boolean };

export function readBookingFlowHistoryState(state: unknown): BookingFlowHistoryState {
  return typeof state === 'object' && state !== null ? (state as BookingFlowHistoryState) : {};
}

const STEP_PATH_BUILDERS: Record<BookingFlowStep, (placeSlugId: string) => string> = {
  [BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE]: ROUTE_BUILDER.bookingFlowSelectService,
  [BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE]: ROUTE_BUILDER.bookingFlowSelectEmployee,
  [BOOKING_FLOW_STEP_SLUGS.ADD_ONS]: ROUTE_BUILDER.bookingFlowAddOns,
  [BOOKING_FLOW_STEP_SLUGS.EXTRA_TIME]: ROUTE_BUILDER.bookingFlowExtraTime,
};

const STEP_ROUTES: Record<BookingFlowStep, string> = {
  [BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE]: APP_ROUTES.BOOKING_FLOW_SELECT_SERVICE,
  [BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE]: APP_ROUTES.BOOKING_FLOW_SELECT_EMPLOYEE,
  [BOOKING_FLOW_STEP_SLUGS.ADD_ONS]: APP_ROUTES.BOOKING_FLOW_ADD_ONS,
  [BOOKING_FLOW_STEP_SLUGS.EXTRA_TIME]: APP_ROUTES.BOOKING_FLOW_EXTRA_TIME,
};

const isOnRoute = (pathname: string, route: string): boolean =>
  matchPath(pathname, { path: route, exact: true }) != null;

/** The step whose route the pathname is on, or undefined off the flow. */
export function getStepFromPathname(pathname: string): BookingFlowStep | undefined {
  return (Object.keys(STEP_ROUTES) as BookingFlowStep[]).find((step) => isOnRoute(pathname, STEP_ROUTES[step]));
}

/** True on the calendar, the flow's non-step page. */
function isCalendarPathname(pathname: string): boolean {
  return isOnRoute(pathname, APP_ROUTES.BOOKING_CALENDAR);
}

function buildStepStamp(
  current: Pick<History, 'location'>['location'],
  targetStep: BookingFlowStep,
): BookingFlowStepHistoryState | undefined {
  const currentStep = getStepFromPathname(current.pathname);
  if (!currentStep) {
    return isCalendarPathname(current.pathname) ? { fromStep: CALENDAR_ORIGIN, backSteps: 1 } : undefined;
  }
  if (currentStep !== targetStep) return { fromStep: currentStep, backSteps: 1 };
  // Same-step push (add-on pick): the previous screen is unchanged, one entry further back.
  const currentStamp = current.state as Partial<BookingFlowStepHistoryState> | undefined;
  return currentStamp?.fromStep
    ? { fromStep: currentStamp.fromStep, backSteps: (currentStamp.backSteps ?? 1) + 1 }
    : undefined;
}

/**
 * Navigates between booking-flow steps, and owns the one exit into the calendar (R13).
 * Step-to-step moves are always a `push`, so back undoes each forward move, with
 * pass-through params preserved via `serialiseSelection`. The one `replace`: a same-step
 * move from an unstamped entry (a deep-linked visitor answering the screen they entered on)
 * mutates that entry in place — a push would mint a second unstamped entry that Avbryt's
 * single replace cannot remove, leaving the aborted flow reachable via browser back
 * (KTD15). The calendar exit always carries `state: { fromLanding: true }`; owning it here
 * keeps every exit identical, unlike the modal where each view sets it (or forgets to) on
 * its own. De-dupes against the current location so two calls for the same target in one
 * tick push a single entry.
 */
export function goToStep(
  history: Pick<History, 'push' | 'replace' | 'location'>,
  placeSlugId: string,
  target: BookingFlowGoToStepTarget,
  patch: BookingFlowSelectionPatch = {},
): void {
  const location = buildTargetLocation(
    placeSlugId,
    target,
    serialiseSelection(history.location.search, patch),
    getStepFromPathname(history.location.pathname),
  );

  if (history.location.pathname === location.pathname && history.location.search === location.search) {
    return;
  }

  if (target.type === 'step') {
    const stamp = buildStepStamp(history.location, target.step);
    if (stamp) {
      history.push({ ...location, state: stamp });
      return;
    }
    if (getStepFromPathname(history.location.pathname) === target.step) {
      history.replace(location);
      return;
    }
  }

  history.push(location);

  if (target.type === 'calendar') {
    // Parity with the modal, which fires searchTime() on every calendar exit.
    bookingFlowTrackingSession.searchTime();
  }
}

/**
 * Place-page entry into the flow (U14). Unlike `goToStep`, this clears any stale flow
 * params from the current URL (a bookmarked `/places/x?ss=1&sr=abc` must not leak into a
 * fresh entry) and seeds `sistaminuten=1` when the entry point knows the visit is
 * last-minute — the param gates pricing and the server auth header, and the flow itself
 * only ever passes it through.
 */
export function enterBookingFlow(
  history: Pick<History, 'push' | 'location'>,
  placeSlugId: string,
  target: BookingFlowGoToStepTarget,
  patch: BookingFlowSelectionPatch,
  { sistaminuten = false }: { sistaminuten?: boolean } = {},
): void {
  saveEntryScroll(placeSlugId);
  const params = new URLSearchParams(history.location.search);
  if (sistaminuten) params.set('sistaminuten', '1');
  const search = serialiseSelection(params.toString(), { ss: null, sr: null, rs: null, et: null, ...patch });
  history.push(buildTargetLocation(placeSlugId, target, search));
}

const ENTRY_SCROLL_KEY = 'bookingFlowEntryScroll';
const ENTRY_SCROLL_TTL_MS = 30 * 60 * 1000;

function saveEntryScroll(placeSlugId: string): void {
  try {
    const placeId = placeSlugId.split('-').pop();
    sessionStorage.setItem(ENTRY_SCROLL_KEY, JSON.stringify({ placeId, y: window.scrollY, at: Date.now() }));
  } catch {
    // Storage unavailable — scroll restoration is best-effort.
  }
}

/**
 * The place-page scroll position saved when the flow was entered, consumed on read. The
 * place page restores it when the user returns (Avbryt or browser back) instead of
 * scrolling to top. Keyed by place id and time-bounded, so an abandoned flow cannot
 * scroll an unrelated later visit.
 */
export function consumeBookingFlowEntryScroll(placeId: number): number | undefined {
  try {
    const raw = sessionStorage.getItem(ENTRY_SCROLL_KEY);
    if (!raw) return undefined;
    const saved = JSON.parse(raw) as { placeId?: string; y?: number; at?: number };
    if (saved.placeId !== String(placeId) || typeof saved.y !== 'number') return undefined;
    if (typeof saved.at !== 'number' || Date.now() - saved.at > ENTRY_SCROLL_TTL_MS) return undefined;
    sessionStorage.removeItem(ENTRY_SCROLL_KEY);
    return saved.y;
  } catch {
    return undefined;
  }
}

/**
 * Builds the history location a target resolves to, exposed so page guards can `<Redirect>`
 * without duplicating the path construction. Calendar targets carry `fromLanding` (R13)
 * plus `fromStep` when entered from a flow step — the calendar reads it to treat backing
 * into a step as a rewind, not a session cancel. Control paths never set `fromStep`.
 */
export function buildTargetLocation(
  placeSlugId: string,
  target: BookingFlowGoToStepTarget,
  search: string,
  fromStep?: BookingFlowStep,
): { pathname: string; search: string; state?: { fromLanding: true; fromStep?: BookingFlowStep } } {
  const pathname =
    target.type === 'calendar'
      ? ROUTE_BUILDER.bookingCalendar(placeSlugId, toSlugId(target.mainService.about?.slug, target.mainService.id))
      : STEP_PATH_BUILDERS[target.step](placeSlugId);

  if (target.type !== 'calendar') return { pathname, search };
  return { pathname, search, state: fromStep ? { fromLanding: true, fromStep } : { fromLanding: true } };
}
